import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles, ChevronDown } from "lucide-react";
import { useLang } from "../i18n";
import { scrollToId } from "../hooks/useLenis";
import { MaskedLine } from "./Reveal";

export function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const logoY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src="/assets/hero-bg.webp" alt="Open-pit mine at dusk" className="h-[120%] w-full object-cover brightness-125" />
        <div className="absolute inset-0 bg-[#07111F]/68" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07111F]/70 via-[#07111F]/25 to-[#07111F]" />
        <div className="absolute inset-0 animate-hero-glow" style={{ background: "radial-gradient(ellipse 72% 55% at 50% 38%, rgba(201,162,39,0.12), transparent 65%)" }} />
      </motion.div>

      <motion.div style={{ opacity: contentOpacity }} className="flex-1 flex flex-col items-center justify-center text-center px-5 pt-24 pb-14">
        <motion.div style={{ y: logoY }} className="relative flex items-center justify-center select-none">
          <div
            aria-hidden="true"
            className="absolute left-1/2 top-1/2 h-[78%] w-[112%] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
            style={{ background: "radial-gradient(ellipse, rgba(7,17,31,0.85), transparent 70%)" }}
          />
          <motion.img
            src="/assets/logo-mx.png"
            alt="MX Mining Exchange — gold M and silver X logo"
            data-testid="hero-logo-mx"
            className="w-[92vw] sm:w-[590px] lg:w-[730px] xl:w-[830px] h-auto animate-logo-glow"
            initial={{ opacity: 0, scale: 0.9, filter: "blur(16px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          />
          <motion.span
            className="absolute top-[12%] left-[16%] text-[#C9A227]"
            animate={{ opacity: [0, 1, 0], scale: [0.6, 1.1, 0.6] }}
            transition={{ duration: 2.6, repeat: Infinity, delay: 1.4 }}
          >
            <Sparkles size={20} />
          </motion.span>
          <motion.span
            className="absolute bottom-[18%] right-[14%] text-[#EDF1F6]"
            animate={{ opacity: [0, 1, 0], scale: [0.6, 1, 0.6] }}
            transition={{ duration: 3.2, repeat: Infinity, delay: 2.2 }}
          >
            <Sparkles size={16} />
          </motion.span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-2"
        >
          <p data-testid="hero-brand" className="font-display text-lg sm:text-xl tracking-[0.5em] text-[#F5F7FA]">{t.hero.brand}</p>
          <p data-testid="hero-tagline" className="mt-2 font-body text-xs sm:text-sm tracking-[0.25em] uppercase text-[#C9A227]">{t.hero.tagline}</p>
        </motion.div>

        <h1 className="mt-8 font-display font-bold leading-[0.95] text-[11vw] sm:text-5xl lg:text-6xl xl:text-7xl" data-testid="hero-headline">
          <MaskedLine delay={1.1}><span className="text-gold-metallic">{t.hero.h1[0]}</span></MaskedLine>
          <MaskedLine delay={1.25}><span className="text-silver-metallic">{t.hero.h1[1]}</span></MaskedLine>
          <MaskedLine delay={1.4}><span className="text-[#F5F7FA]">{t.hero.h1[2]}</span></MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subtext"
          className="mt-6 max-w-xl text-sm sm:text-base text-[#EDF1F6] leading-relaxed"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.8 }}
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          className="mt-8 flex flex-col sm:flex-row items-center gap-4"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 2 }}
        >
          <button
            data-testid="hero-cta-request"
            onClick={() => scrollToId("#request")}
            className="rounded-full bg-[#C9A227] px-9 py-4 font-body text-sm font-bold tracking-[0.15em] text-[#07111F] hover:bg-[#F5F7FA] hover:shadow-[0_0_35px_rgba(201,162,39,0.45)] hover:-translate-y-0.5 transition-[background-color,box-shadow,transform] duration-300"
          >
            {t.hero.cta1}
          </button>
          <button
            data-testid="hero-cta-prices"
            onClick={() => scrollToId("#prices")}
            className="rounded-full border border-[#EDF1F6]/40 px-9 py-4 font-body text-sm font-semibold tracking-[0.15em] text-[#F5F7FA] hover:border-[#C9A227] hover:text-[#C9A227] hover:-translate-y-0.5 transition-[border-color,color,transform] duration-300"
          >
            {t.hero.cta2}
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#minerals")}
        className="relative z-10 mx-auto mb-8 flex flex-col items-center gap-2 text-[#EDF1F6]/70 hover:text-[#C9A227] transition-colors duration-300"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.6 }}
        aria-label={t.hero.scroll}
      >
        <span className="text-[10px] tracking-[0.35em] uppercase">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ChevronDown size={18} />
        </motion.span>
      </motion.button>
    </section>
  );
}
